import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileCheck,
  Flag,
  Sparkles,
  BarChart3,
  Target,
  Lightbulb,
  RefreshCw,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color coding for accuracy
const getAccuracyColor = (accuracy) => {
  if (accuracy === null) return 'text-gray-500';
  if (accuracy >= 85) return 'text-emerald-600';
  if (accuracy >= 70) return 'text-green-600';
  if (accuracy >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

const getAccuracyBg = (accuracy) => {
  if (accuracy === null) return 'bg-gray-100';
  if (accuracy >= 85) return 'bg-emerald-50 border-emerald-200';
  if (accuracy >= 70) return 'bg-green-50 border-green-200';
  if (accuracy >= 50) return 'bg-yellow-50 border-yellow-200';
  return 'bg-red-50 border-red-200';
};

const AIAccuracyReport = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [trends, setTrends] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [reportRes, trendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sports-services/admin/ai-accuracy-report`),
        axios.get(`${API_URL}/api/sports-services/admin/ai-accuracy-report/trends`).catch(() => ({ data: null })),
      ]);
      setReport(reportRes.data);
      setTrends(trendsRes.data);
    } catch (err) {
      console.error('Failed to fetch AI accuracy report:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!report) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Brain className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">No AI Analysis Data Yet</h3>
          <p className="text-gray-500 mt-2">Run AI pre-approval on some verifications to see accuracy metrics</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-accuracy-report">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-purple-600" />
            AI Accuracy Report
          </h2>
          <p className="text-gray-500">Document-level performance analysis for AI pre-approval</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchReport}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {report.summary?.overall_accuracy || 0}%
                </div>
                <p className="text-sm text-gray-500">Overall Accuracy</p>
              </div>
              <Target className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-blue-600">
                  {report.summary?.total_documents || 0}
                </div>
                <p className="text-sm text-gray-500">Documents Analyzed</p>
              </div>
              <FileCheck className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        {report.summary?.best_performing && (
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Best Performing</span>
                </div>
                <p className="text-lg font-bold text-gray-800 mt-1">
                  {report.summary.best_performing.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-sm text-gray-500">{report.summary.best_performing.accuracy}% accuracy</p>
              </div>
            </CardContent>
          </Card>
        )}

        {report.summary?.worst_performing && (
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div>
                <div className="flex items-center gap-1">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-600">Needs Improvement</span>
                </div>
                <p className="text-lg font-bold text-gray-800 mt-1">
                  {report.summary.worst_performing.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-sm text-gray-500">{report.summary.worst_performing.accuracy}% accuracy</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-1" />
            By Document Type
          </TabsTrigger>
          <TabsTrigger value="recommendations">
            <Lightbulb className="h-4 w-4 mr-1" />
            Recommendations ({report.recommendations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="feedback">
            <Flag className="h-4 w-4 mr-1" />
            Override History ({report.feedback?.total_overrides || 0})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - By Document Type */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {report.document_types?.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-gray-500">No document analysis data available yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {report.document_types?.map((doc, idx) => (
                <Card 
                  key={doc.document_type}
                  className={`border ${getAccuracyBg(doc.accuracy_percent)}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{doc.display_name}</h3>
                          {doc.needs_improvement && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Needs Improvement
                            </Badge>
                          )}
                          {doc.accuracy_percent >= 85 && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Excellent
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                          <div>
                            <p className="text-xs text-gray-500">Analyzed</p>
                            <p className="text-lg font-semibold">{doc.total_analyzed}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Avg Confidence</p>
                            <p className="text-lg font-semibold">{doc.average_confidence}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Flags Found</p>
                            <p className="text-lg font-semibold flex items-center gap-1">
                              {doc.flags_count}
                              {doc.flags_count > 5 && <Flag className="h-4 w-4 text-red-500" />}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Overrides</p>
                            <p className="text-lg font-semibold">{doc.override_count}</p>
                          </div>
                        </div>

                        {/* Risk Distribution */}
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-1">Risk Distribution</p>
                          <div className="flex gap-2">
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              Low: {doc.risk_distribution?.low || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                              Medium: {doc.risk_distribution?.medium || 0}
                            </Badge>
                            <Badge variant="outline" className="bg-red-50 text-red-700">
                              High: {doc.risk_distribution?.high || 0}
                            </Badge>
                          </div>
                        </div>

                        {/* Top Issues */}
                        {doc.top_issues?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-1">Common Issues</p>
                            <div className="flex flex-wrap gap-1">
                              {doc.top_issues.map((issue, i) => (
                                <Badge 
                                  key={i} 
                                  variant="outline" 
                                  className="text-xs bg-gray-50"
                                >
                                  {issue.issue} ({issue.count})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Accuracy Score */}
                      <div className="text-center ml-4">
                        <div className={`text-4xl font-bold ${getAccuracyColor(doc.accuracy_percent)}`}>
                          {doc.accuracy_percent !== null ? `${doc.accuracy_percent}%` : 'N/A'}
                        </div>
                        <p className="text-xs text-gray-500">Accuracy</p>
                        {doc.total_decided > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            {doc.correct_predictions}/{doc.total_decided} correct
                          </p>
                        )}
                        {doc.accuracy_percent !== null && (
                          <Progress 
                            value={doc.accuracy_percent} 
                            className="w-20 h-2 mt-2" 
                          />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                AI Improvement Recommendations
              </CardTitle>
              <CardDescription>
                Suggestions based on AI performance analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {report.recommendations?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">All document types are performing well!</p>
                  <p className="text-sm text-gray-400 mt-1">No recommendations at this time</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {report.recommendations?.map((rec, idx) => (
                    <div 
                      key={idx}
                      className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-yellow-900">
                            {rec.document_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-sm text-yellow-700">{rec.issue}</p>
                          <p className="text-sm text-yellow-600 mt-1">
                            💡 {rec.suggestion}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Override History Tab */}
        <TabsContent value="feedback" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-orange-500" />
                Admin Override History
              </CardTitle>
              <CardDescription>
                Cases where admin overrode AI decisions - helps improve AI accuracy
              </CardDescription>
            </CardHeader>
            <CardContent>
              {report.feedback?.recent_overrides?.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                  <p className="text-gray-600">No AI overrides recorded yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    When admins disagree with AI decisions, those cases appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {report.feedback?.recent_overrides?.map((override, idx) => (
                    <div 
                      key={idx}
                      className="p-4 border rounded-lg bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-orange-50 text-orange-700">
                              {override.original_ai_status?.replace('_', ' ')}
                            </Badge>
                            <span className="text-gray-400">→</span>
                            <Badge variant="outline" className={
                              override.admin_decision === 'approve' 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-red-50 text-red-700'
                            }>
                              {override.admin_decision}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">Reason:</span> {override.override_reason}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Original confidence: {override.original_ai_confidence}%
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          {new Date(override.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Trends Section */}
      {trends && trends.trends?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              AI Performance Trends
            </CardTitle>
            <CardDescription>
              Weekly analysis performance over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Period</th>
                    <th className="text-center py-2 px-3">Analyzed</th>
                    <th className="text-center py-2 px-3">Avg Confidence</th>
                    <th className="text-center py-2 px-3">Verified</th>
                    <th className="text-center py-2 px-3">Rejected</th>
                    <th className="text-center py-2 px-3">Decision Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {trends.trends.map((t, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium">{t.period}</td>
                      <td className="text-center py-2 px-3">{t.documents_analyzed}</td>
                      <td className="text-center py-2 px-3">
                        <span className={getAccuracyColor(t.average_confidence)}>
                          {t.average_confidence}%
                        </span>
                      </td>
                      <td className="text-center py-2 px-3 text-green-600">{t.verified}</td>
                      <td className="text-center py-2 px-3 text-red-600">{t.rejected}</td>
                      <td className="text-center py-2 px-3">{t.decision_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIAccuracyReport;
